import { Button, Card, Col, Descriptions, Popconfirm, Row, Space, Spin, Tag, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { OwnerType, SaleResult } from '@never2busy/shared';
import { salesApi } from '../../../api/endpoints/sales';
import { PageHeader } from '../../../components/common/PageHeader';
import { KeywordsManager } from '../../../components/common/KeywordsManager';
import { useSaleStageLabels } from '../useSaleStageLabels';

const RESULT_COLOR: Record<SaleResult, string> = {
  [SaleResult.PENDING]: 'processing',
  [SaleResult.WON]: 'success',
  [SaleResult.LOST]: 'error',
};

const RESULT_LABEL: Record<SaleResult, string> = {
  [SaleResult.PENDING]: 'Pendiente',
  [SaleResult.WON]: 'Ganada',
  [SaleResult.LOST]: 'Perdida',
};

export function SaleDetailPage(): JSX.Element {
  const { id } = useParams();
  const saleId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stageLabels = useSaleStageLabels();

  const { data, isLoading } = useQuery({
    queryKey: ['sale', saleId],
    queryFn: () => salesApi.findOne(saleId),
  });

  const setResultMutation = useMutation({
    mutationFn: (result: SaleResult) => salesApi.setResult(saleId, result),
    onSuccess: () => {
      message.success('Resultado actualizado');
      void queryClient.invalidateQueries({ queryKey: ['sale', saleId] });
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => message.error('No se pudo actualizar'),
  });

  const removeMutation = useMutation({
    mutationFn: () => salesApi.remove(saleId),
    onSuccess: () => {
      message.success('Venta eliminada');
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      navigate('/ventas');
    },
    onError: () => message.error('No se pudo eliminar la venta'),
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!data) return <div>Venta no encontrada</div>;

  return (
    <>
      <PageHeader
        title={data.saleNumber ? `${data.saleNumber} · ${data.name}` : data.name}
        crumbs={[{ title: 'Ventas', to: '/ventas' }, { title: data.name }]}
        extra={
          <Space wrap>
            {data.result === SaleResult.PENDING && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => setResultMutation.mutate(SaleResult.WON)}
                  loading={setResultMutation.isPending}
                >
                  Marcar como ganada
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => setResultMutation.mutate(SaleResult.LOST)}
                  loading={setResultMutation.isPending}
                >
                  Perdida
                </Button>
              </>
            )}
            {data.result !== SaleResult.PENDING && (
              <Button onClick={() => setResultMutation.mutate(SaleResult.PENDING)}>
                Reabrir
              </Button>
            )}
            <Button icon={<EditOutlined />} onClick={() => navigate(`/ventas/${data.id}/editar`)}>
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar esta venta?"
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={removeMutation.isPending}>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card>
            <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
              <Descriptions.Item label="Etapa">
                {data.stage ? <Tag>{stageLabels[data.stage] ?? data.stage}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Resultado">
                <Tag color={RESULT_COLOR[data.result]}>{RESULT_LABEL[data.result]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Producto">{data.product ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Monto">
                {data.amount
                  ? `${parseFloat(data.amount).toLocaleString('es-MX', {
                      minimumFractionDigits: 2,
                    })} ${data.currency?.code ?? ''}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Probabilidad">
                {data.probability != null ? `${data.probability}%` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha esperada">
                {data.saleDate ? dayjs(data.saleDate).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="No. OC">{data.poNumber ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Cuenta">
                {data.account ? (
                  <Link to={`/cuentas/${data.account.id}`}>{data.account.name}</Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Contacto">
                {data.person ? (
                  <Link to={`/contactos/${data.person.id}`}>
                    {data.person.firstName ?? ''} {data.person.lastName}
                  </Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Proyecto">
                {data.project ? (
                  <Link to={`/proyectos/${data.project.id}`}>
                    {data.project.projectNumber
                      ? `${data.project.projectNumber} · ${data.project.name}`
                      : data.project.name}
                  </Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Asignada a">
                {data.assignedTo
                  ? `${data.assignedTo.firstName} ${data.assignedTo.lastName}`
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
            {data.notes && (
              <div style={{ marginTop: 16 }}>
                <strong>Notas:</strong>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{data.notes}</div>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <strong>Etiquetas:</strong>
              <div style={{ marginTop: 8 }}>
                <KeywordsManager ownerType={OwnerType.SALE} ownerId={data.id} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
}
